import { useEffect, useState } from "react";
import { Bell, BellRing, BellOff } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/** Convert base64url string into a Uint8Array for Push Manager. */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

/**
 * Enables / disables Web Push notifications. Hidden when the browser lacks
 * support or when the backend is missing VAPID keys.
 */
export default function PushNotificationsToggle({ className = "" }) {
  const [supported, setSupported] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(ok);
    if (!ok) return;
    api.get("/push/config").then(({ data }) => setConfig(data)).catch(() => {});
    navigator.serviceWorker.getRegistration().then(async (reg) => {
      const r = reg || (await navigator.serviceWorker.ready);
      const sub = await r.pushManager.getSubscription();
      setSubscribed(!!sub);
    }).catch(() => {});
  }, []);

  if (!supported || !config?.configured) return null;

  const subscribe = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Permission denied for notifications.");
        return;
      }
      const reg = (await navigator.serviceWorker.getRegistration()) || (await navigator.serviceWorker.ready);
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.public_key),
      });
      const payload = sub.toJSON();
      await api.post("/push/subscribe", {
        endpoint: payload.endpoint,
        keys: payload.keys,
      });
      setSubscribed(true);
      toast.success("Push notifications enabled");
      // Fire a confirmation push so the user knows it works
      api.post("/push/test").catch(() => {});
    } catch (err) {
      toast.error(err?.message || "Could not enable notifications");
    } finally {
      setBusy(false);
    }
  };

  const unsubscribe = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint }).catch(() => {});
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Push notifications disabled");
    } finally {
      setBusy(false);
    }
  };

  const Icon = subscribed ? BellRing : Bell;
  return (
    <button
      type="button"
      onClick={subscribed ? unsubscribe : subscribe}
      disabled={busy}
      data-testid="push-toggle-btn"
      title={subscribed ? "Disable browser notifications" : "Enable browser notifications"}
      className={`text-sm px-3 py-2 border flex items-center gap-1.5 transition-colors disabled:opacity-60 ${className}`}
      style={{
        borderColor: subscribed ? "#10B981" : "var(--ll-border)",
        background: subscribed ? "#ECFDF5" : "white",
        color: subscribed ? "#065F46" : "var(--ll-ink)",
      }}
    >
      {busy ? <BellOff size={14} className="animate-pulse" /> : <Icon size={14} />}
      {subscribed ? "Notifications on" : "Enable notifications"}
    </button>
  );
}
